<template>
	<div class="flex flex-col gap-4">
		<AppCard title="Adres">
			<div class="grid grid-cols-[1fr_300px] gap-4 py-4">
				<div class="flex flex-col gap-4">
					<AppAddressInput v-model="object.address" class="w-full" />
				</div>
				<div>
					<img
						v-if="imageUrl"
						:src="imageUrl"
						class="w-full aspect-3/2 object-cover rounded"
						referrerpolicy="no-referrer"
					/>
					<div
						v-else-if="object.address"
						class="w-full aspect-3/2 bg-gray-100 rounded"
					/>
				</div>
			</div>
		</AppCard>

		<AppCard title="Objectkenmerken">
			<PropertyList>
				<PropertyField v-model="object.objectType" label="Objectsoort">
					<DropdownInput
						v-model="object.objectType"
						class="text-sm"
						:options="objectTypeOptions"
					/>
				</PropertyField>

				<PropertyField
					v-model="object.specialObjectType"
					label="Bijzonder objectsoort"
				>
					<DropdownInput
						v-model="object.specialObjectType"
						class="text-sm"
						:options="specialObjectTypeOptions"
					/>
				</PropertyField>

				<PropertyField v-model="object.garagePresent" label="Garage aanwezig">
					<OptionInput
						v-model="object.garagePresent"
						class="text-sm"
						:options="yesNoOptions"
					/>
				</PropertyField>

				<PropertyField v-model="object.ownerOccupied" label="Eigen bewoning">
					<OptionInput
						v-model="object.ownerOccupied"
						class="text-sm"
						:options="yesNoOptions"
					/>
				</PropertyField>

				<PropertyField
					v-model="object.recreationalUse"
					label="Recreatief gebruik"
				>
					<OptionInput
						v-model="object.recreationalUse"
						class="text-sm"
						:options="yesNoOptions"
					/>
				</PropertyField>
			</PropertyList>
		</AppCard>

		<AppCard title="Waarde">
			<PropertyList>
				<PropertyField v-model="object.marketValue" label="Marktwaarde">
					<template #sources>
						<DataSourceButton
							v-if="addressMeta?.wozWaarden?.[0]"
							:value="addressMeta.wozWaarden[0].vastgesteldeWaarde"
							:current-value="object.marketValue"
							source="wozWaardeLoket"
							tooltip="WOZ-waarde is een indicatie, geen taxatie"
							:on-apply="applyWozToMarketValue"
						/>
					</template>
					<AppFieldNumeric
						v-model="object.marketValue"
						:value-type="ValueType.Currency"
						:precision="0"
						:grouping="true"
						prefix="€"
						wrapper-class="w-[200px]"
					/>
				</PropertyField>

				<PropertyField
					v-model="object.valuationSource"
					label="Bron waardebepaling"
				>
					<DropdownInput
						v-model="object.valuationSource"
						class="text-sm"
						:options="valuationSourceOptions"
					/>
				</PropertyField>

				<PropertyRow
					v-if="addressMeta?.wozWaarden?.length"
					label="WOZ-waarde"
					dd-class="text-sm"
				>
					<template #actions>
						<DataSourceButton
							v-if="addressMeta?.wozWaarden?.[0]"
							:value="addressMeta.wozWaarden[0].vastgesteldeWaarde"
							:current-value="object.wozValue"
							source="wozWaardeLoket"
							:on-apply="applyWozToCurrentObject"
						/>
					</template>
					<table class="w-full">
						<thead>
							<tr class="text-left">
								<th>Peildatum</th>
								<th>WOZ-waarde</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="woz in addressMeta.wozWaarden.slice(
									0,
									showAllWoz ? addressMeta.wozWaarden.length : 3,
								)"
								:key="woz.peildatum"
							>
								<td>{{ simpleDate(woz.peildatum) }}</td>
								<td>{{ asDisplayCur(woz.vastgesteldeWaarde) }}</td>
							</tr>
						</tbody>
					</table>
					<AppButton
						variant="link"
						class="self-start"
						@click="showAllWoz = !showAllWoz"
					>
						{{ showAllWoz ? "< Minder weergeven" : "> Alles weergeven" }}
					</AppButton>
				</PropertyRow>

				<PropertyField v-else v-model="object.wozValue" label="WOZ-waarde">
					<AppFieldNumeric
						v-model="object.wozValue"
						:value-type="ValueType.Currency"
						:precision="0"
						:grouping="true"
						prefix="€"
						wrapper-class="w-[200px]"
					/>
				</PropertyField>
			</PropertyList>
		</AppCard>

		<AppCard title="Verkoopstatus">
			<PropertyList>
				<PropertyField v-model="object.saleStatus.toBeSold" label="Te verkopen">
					<OptionInput
						v-model="object.saleStatus.toBeSold"
						class="text-sm"
						:options="yesNoOptions"
					/>
				</PropertyField>

				<PropertyField v-model="object.saleStatus.sold" label="Verkocht">
					<OptionInput
						v-model="object.saleStatus.sold"
						class="text-sm"
						:options="yesNoOptions"
					/>
				</PropertyField>

				<template v-if="object.saleStatus.toBeSold || object.saleStatus.sold">
					<PropertyField
						v-model="object.saleStatus.hasSaleContract"
						label="Koopovereenkomst getekend"
					>
						<OptionInput
							v-model="object.saleStatus.hasSaleContract"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyField>

					<PropertyField
						v-model="object.saleStatus.salePrice"
						label="Verkoopprijs"
					>
						<AppFieldNumeric
							v-model="object.saleStatus.salePrice"
							:value-type="ValueType.Currency"
							:precision="0"
							:grouping="true"
							prefix="€"
							wrapper-class="w-[200px]"
						/>
					</PropertyField>

					<PropertyField
						v-model="object.saleStatus.saleCosts"
						label="Verkoopkosten"
					>
						<AppFieldNumeric
							v-model="object.saleStatus.saleCosts"
							:value-type="ValueType.Currency"
							:precision="0"
							:grouping="true"
							prefix="€"
							wrapper-class="w-[200px]"
						/>
					</PropertyField>

					<PropertyField
						v-model="object.saleStatus.saleDate"
						label="Leveringsdatum"
					>
						<AppFieldDate
							v-model="object.saleStatus.saleDate"
							class="w-[200px]"
						/>
					</PropertyField>

					<PropertyField
						v-model="object.saleStatus.hasConditions"
						label="Verkoop onder voorwaarden"
					>
						<OptionInput
							v-model="object.saleStatus.hasConditions"
							class="text-sm"
							:options="yesNoOptions"
						/>
					</PropertyField>

					<template v-if="object.saleStatus.hasConditions">
						<PropertyField
							v-model="object.saleStatus.contingencyDate"
							label="Ontbindende voorwaarden datum"
						>
							<AppFieldDate
								v-model="object.saleStatus.contingencyDate"
								class="w-[200px]"
							/>
						</PropertyField>
					</template>
				</template>
			</PropertyList>
		</AppCard>

		<AppCard title="Verhuur">
			<PropertyList>
				<PropertyField
					v-model="object.rentalDetails.hasRentalAgreement"
					label="Huurovereenkomst"
				>
					<OptionInput
						v-model="object.rentalDetails.hasRentalAgreement"
						class="text-sm"
						:options="yesNoOptions"
					/>
				</PropertyField>

				<template v-if="object.rentalDetails.hasRentalAgreement">
					<PropertyField
						v-model="object.rentalDetails.rentalType"
						label="Soort verhuur"
					>
						<DropdownInput
							v-model="object.rentalDetails.rentalType"
							class="text-sm"
							:options="rentalTypeOptions"
						/>
					</PropertyField>

					<PropertyField
						v-model="object.rentalDetails.rentalStartDate"
						label="Ingangsdatum"
					>
						<AppFieldDate
							v-model="object.rentalDetails.rentalStartDate"
							class="w-[200px]"
						/>
					</PropertyField>

					<PropertyField
						v-model="object.rentalDetails.annualRentalIncome"
						label="Jaarlijkse huurinkomsten"
					>
						<AppFieldNumeric
							v-model="object.rentalDetails.annualRentalIncome"
							:value-type="ValueType.Currency"
							:precision="0"
							:grouping="true"
							prefix="€"
							wrapper-class="w-[200px]"
						/>
					</PropertyField>
				</template>
			</PropertyList>
		</AppCard>

		<!-- Overbrugging tijdelijk verborgen; mapping loopt via financieringsopzet. -->

		<AppCard title="Eigenaar">
			<PropertyList>
				<PropertyField v-model="object.owner" label="Eigenaar">
					<DropdownInput
						v-model="object.owner"
						class="text-sm"
						:options="ownerOptions"
					/>
				</PropertyField>
			</PropertyList>
		</AppCard>
	</div>
</template>

<script setup lang="ts">
import type {
	CurrentObject,
	CurrentObjectBridgingDetails,
	CurrentObjectRentalDetails,
	CurrentObjectSaleStatus,
} from "@loanlink-nl/model-app";
import { storeToRefs } from "pinia";
import { computed, ref, watch, watchEffect } from "vue";
import { ValueType } from "@/enums/ValueType";
import { hdnListToOptions } from "@/helpers/hdnHelpers";
import {
	ObjectSoortType,
	BijzObjectSoortType,
	BronWaardebepalingType,
	VerhuurType,
} from "@loanlink-nl/model-hdn/25.0/OfferteAanvraag";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { api } from "@/loanLinkApi";
import { addressLine5, addressLine6, simpleDate } from "@/helpers/uiHelpers";
import { asDisplayCur } from "@/helpers/inputHelpers";

const model = defineModel<CurrentObject>({ required: true });

const dossierDataStore = useDossierDataStore();
const { data } = storeToRefs(dossierDataStore);

interface ReactiveCurrentObject extends Omit<
	CurrentObject,
	"saleStatus" | "rentalDetails" | "bridgingDetails"
> {
	saleStatus: CurrentObjectSaleStatus;
	rentalDetails: CurrentObjectRentalDetails;
	bridgingDetails: CurrentObjectBridgingDetails;
}

function ensureObjectState(value: CurrentObject): ReactiveCurrentObject {
	if (!value.saleStatus) {
		value.saleStatus = {};
	}

	if (!value.rentalDetails) {
		value.rentalDetails = {};
	}

	if (!value.bridgingDetails) {
		value.bridgingDetails = {};
	}

	return value as ReactiveCurrentObject;
}

watchEffect(() => {
	ensureObjectState(model.value);
});

const object = computed<ReactiveCurrentObject>(() =>
	ensureObjectState(model.value),
);

// Address meta for image lookup
const addressMeta = ref<any>(null);

watch(
	() => object.value.address?.adresseerbaarObjectIdentificatie,
	async (id) => {
		if (id) {
			const requestedId = id;
			const { data: meta } = await api.address.addressList(id);
			// Stale response guard: als het adres ondertussen gewijzigd is, negeer het resultaat
			if (
				object.value.address?.adresseerbaarObjectIdentificatie !== requestedId
			)
				return;
			addressMeta.value = meta;
		} else {
			addressMeta.value = null;
		}
	},
	{ immediate: true },
);

const showAllWoz = ref(false);

function applyWozToCurrentObject(wozWaarde?: number) {
	if (wozWaarde !== undefined) {
		object.value.wozValue = wozWaarde;
	}
}

function applyWozToMarketValue(wozWaarde?: number) {
	if (wozWaarde === undefined) {
		return;
	}
	object.value.marketValue = wozWaarde;
	object.value.valuationSource = BronWaardebepalingType["02 WOZ"];
}

const mostRecentListing = computed(() => {
	if (!addressMeta.value?.listings) return null;
	return addressMeta.value.listings.toSorted(
		(a: any, b: any) =>
			new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
	)[0];
});

const fundaImage = computed(() => {
	if (!mostRecentListing.value?.funda_image_ids) return null;
	const ids = JSON.parse(mostRecentListing.value.funda_image_ids);
	const firstEntry = ids[0];
	if (!firstEntry) return null;
	return typeof firstEntry === "string" ? firstEntry : firstEntry.id;
});

const imageUrl = computed(() => {
	if (fundaImage.value) {
		return `https://cloud.funda.nl/valentina_media/${fundaImage.value}.jpg`;
	}
	if (addressMeta.value && object.value.address) {
		return `https://maps.googleapis.com/maps/api/streetview?size=640x427&location=${addressLine5(object.value.address)} ${addressLine6(object.value.address)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`;
	}
	return null;
});

const yesNoOptions = [
	{ value: true, label: "Ja" },
	{ value: false, label: "Nee" },
];

const objectTypeOptions = [
	{ value: undefined, label: "Selecteren..." },
	...hdnListToOptions(ObjectSoortType, { shouldSort: true }),
];

const specialObjectTypeOptions = [
	{ value: undefined, label: "Geen" },
	...hdnListToOptions(BijzObjectSoortType, { shouldSort: true }),
];

const valuationSourceOptions = [
	{ value: undefined, label: "Selecteren..." },
	...hdnListToOptions(BronWaardebepalingType, { shouldSort: false }),
];

const rentalTypeOptions = [
	{ value: undefined, label: "Selecteren..." },
	...hdnListToOptions(VerhuurType, { shouldSort: false }),
];

const ownerOptions = computed(() => {
	const customers = data.value.customers ?? [];
	const options: { value: string | undefined; label: string }[] = [
		{ value: undefined, label: "Selecteren..." },
	];

	if (customers.length >= 2) {
		options.push({ value: "both", label: "Beide klanten" });
	}

	for (const customer of customers) {
		if (customer.id) {
			const name =
				[customer.personDetails?.firstName, customer.personDetails?.lastName]
					.filter(Boolean)
					.join(" ") || `Klant ${customer.id}`;
			options.push({ value: customer.id, label: name });
		}
	}

	return options;
});
</script>
